import { useEffect, useId, useRef, useState } from 'react'
import type { CameraStatus, Cluster, Site } from '../types'
import { fetchCameras, imageUrl } from '../api'
import { allowsCamera, allowsSite, type SiteFilter } from '../siteFilter'
import type { SiteHealth } from '../useCameraHealth'
import { OfflineCameraIcon } from './OfflineCameraIcon'
import { isInProgress, isStillGrowing } from '../activity'
import { useFinishTick } from '../useFinishTick'
import { relTime } from '../relTime'
import { tamperLabel } from '../tamperSignal'
import type { SiteTamper } from '../siteIssues'
import type { ArmState, CameraTamper } from '../api'
import { ArmSwitch } from './ArmSwitch'
import { armsNext } from '../armTime'
import { wallColumns } from '../wallColumns'
import { useLiveStill } from '../useLiveStill'

// Retries once on 404 — a picture written just after the tile drew — before
// giving up.
function TileThumb({ cluster, camera }: { cluster: Cluster; camera: string }) {
  // Keyed by the snapshot identity so a new one starts fresh by derivation.
  // Resetting this from an effect instead caused a cascading render.
  const key = `${cluster.id}:${cluster.end_time}`
  const [state, setState] = useState({ key, retry: 0, failed: false })
  const { retry, failed } = state.key === key ? state : { retry: 0, failed: false }

  const handleError = () => {
    if (retry === 0) {
      setTimeout(() => setState({ key, retry: 1, failed: false }), 1500)
    } else {
      setState({ key, retry, failed: true })
    }
  }

  if (failed) {
    return (
      <div className="w-full aspect-video bg-zinc-950 flex items-center justify-center text-xs text-zinc-400">
        Waiting for a picture…
      </div>
    )
  }

  return (
    <img
      key={retry}
      src={`${imageUrl(cluster.primary_event_id ?? cluster.last_event_id, cluster.end_time)}${retry ? `&r=${retry}` : ''}`}
      alt={camera}
      className="w-full aspect-video object-contain bg-zinc-950"
      loading="lazy"
      onError={handleError}
    />
  )
}

interface Props {
  sites: Record<string, Site>
  filter: SiteFilter
  clusters: Cluster[]
  cardSize: number
  health: SiteHealth
  /** Absent means nothing known to be tampered, which renders as before. */
  tamper?: SiteTamper
  onSelectCamera: (siteId: string, camera: string) => void
  /** A site with no arm state shows no switch. */
  armed?: Record<string, ArmState>
  onSetArmed?: (siteId: string, armed: boolean) => void
  armBusy?: Set<string>
  /** Whether the site list has loaded, so an empty wall is not read as no sites. */
  status?: 'loading' | 'ok' | 'error'
  /** A tile to focus once drawn, `site/camera`: the one a closed camera was opened from. */
  focusTile?: string | null
  onTileFocused?: () => void
}

// A Chip is one state written on a tile. Written rather than a coloured dot: a
// degraded camera had only a tooltip and looked healthy (#277).
interface Chip {
  text: string
  className: string
  pulse?: boolean
}

const chipOnPicture = 'bg-black/65 border border-transparent'

export function SiteDashboard({ sites, clusters, filter, cardSize, health, tamper = {}, onSelectCamera, armed, onSetArmed, armBusy, status = 'ok', focusTile, onTileFocused }: Props) {
  useFinishTick(clusters)
  const ids = useId()
  // From each site's camera list rather than its incidents, so every
  // configured camera gets a tile.
  const [camerasBySite, setCamerasBySite] = useState<Record<string, string[]>>({})
  // Sites whose camera list could not be had: unreachable, not empty.
  const [unlisted, setUnlisted] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    Object.keys(sites).forEach((siteId) => {
      fetchCameras(siteId)
        // An older VMS answers null for a site with no cameras.
        .then((cams) => {
          if (cancelled) return
          setCamerasBySite((prev) => ({ ...prev, [siteId]: cams ?? [] }))
          setUnlisted((prev) => { if (!prev.has(siteId)) return prev; const next = new Set(prev); next.delete(siteId); return next })
        })
        .catch(() => {
          if (cancelled) return
          setCamerasBySite((prev) => ({ ...prev, [siteId]: prev[siteId] ?? [] }))
          setUnlisted((prev) => new Set(prev).add(siteId))
        })
    })
    return () => { cancelled = true }
  }, [sites])

  const tilesRoot = useRef<HTMLDivElement>(null)
  // The wall's width decides its columns at each tile size (wallColumns).
  const [wallWidth, setWallWidth] = useState<number | null>(null)
  const [wallEl, setWallEl] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!wallEl || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => setWallWidth(entries[0].contentRect.width))
    observer.observe(wallEl)
    return () => observer.disconnect()
  }, [wallEl])
  // The tile arrives once its site's cameras are listed again, which can be a
  // render or two after the wall comes back.
  useEffect(() => {
    if (!focusTile) return
    const tile = tilesRoot.current?.querySelector<HTMLElement>(`[data-tile="${CSS.escape(focusTile)}"]`)
    if (!tile) return
    tile.focus()
    onTileFocused?.()
  }, [focusTile, camerasBySite, onTileFocused])

  const latestPerCamera = new Map<string, Cluster>()
  for (const c of clusters) {
    const key = `${c.site_id}:${c.camera}`
    const existing = latestPerCamera.get(key)
    if (!existing || c.end_time > existing.end_time) latestPerCamera.set(key, c)
  }

  const siteIds = Object.keys(sites).sort((a, b) => (sites[a].name || a).localeCompare(sites[b].name || b))

  const visibleSiteIds = siteIds.filter((id) => allowsSite(filter, id))

  if (siteIds.length === 0) {
    // The Sites sidebar said each of these; the wall read all three as an
    // empty account (#287).
    if (status === 'loading') return <p className="text-center text-zinc-400 mt-20">Loading sites…</p>
    if (status === 'error') return <p role="alert" className="text-center text-fault mt-20">Couldn’t load your sites. Retrying every 30 seconds.</p>
    return <p className="text-center text-zinc-400 mt-20">No sites configured.</p>
  }

  return (
    <div ref={(el) => { tilesRoot.current = el; setWallEl(el) }} className="flex flex-col gap-7">
      {visibleSiteIds.map((siteId) => {
        const site = sites[siteId]
        const cameras = camerasBySite[siteId]?.filter((cam) => allowsCamera(filter, siteId, cam))
        const count = (pick: (cam: string) => boolean) => cameras?.filter(pick).length ?? 0
        const counts = [
          { n: count((c) => health[siteId]?.[c] === 'offline'), words: 'offline', dot: 'bg-fault' },
          { n: count((c) => health[siteId]?.[c] === 'degraded'), words: 'degraded', dot: 'bg-degraded' },
          { n: count((c) => !!tamper[siteId]?.[c]), words: 'tampered', dot: 'bg-attention' },
        ].filter((c) => c.n > 0)
        const arm = armed?.[siteId]
        const nextArm = arm ? armsNext(arm) : null
        const heading = `${ids}-${siteId}`
        return (
          <section key={siteId} data-site-id={siteId} aria-labelledby={heading} className="flex flex-col gap-3 scroll-mt-4">
            {/* A rule under the header, not just a gap above it: with several
                sites stacked, one site's cameras ran straight into the next's. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2.5 border-b border-zinc-800">
              <h2 id={heading} className="text-base font-semibold text-zinc-100 truncate min-w-0">
                {site.name || siteId}
              </h2>
              {cameras !== undefined && cameras.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm text-zinc-400 tabular-nums">
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
                    {cameras.length} {cameras.length === 1 ? 'camera' : 'cameras'}
                  </span>
                  {counts.map((c) => (
                    <span key={c.words} className="flex items-center gap-1.5">
                      <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {`${c.n} ${c.words}`}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex-1" />
              {arm && onSetArmed && (
                <div className="flex items-center gap-2.5">
                  {nextArm && <span className="text-sm text-zinc-400 tabular-nums">{nextArm}</span>}
                  <ArmSwitch site={site} state={arm} busy={armBusy?.has(siteId) ?? false} onSetArmed={onSetArmed} />
                </div>
              )}
            </div>
            {cameras === undefined ? (
              <p className="text-sm text-zinc-400">Loading cameras…</p>
            ) : cameras.length === 0 && unlisted.has(siteId) ? (
              <p className="text-sm text-fault">Unreachable — its cameras can’t be listed.</p>
            ) : cameras.length === 0 ? (
              <p className="text-sm text-zinc-400">No cameras</p>
            ) : (
              <div className="grid gap-3.5" style={{
                // auto-fill only until the wall is measured, or where there is no ResizeObserver.
                gridTemplateColumns: wallWidth ? `repeat(${wallColumns(wallWidth, cardSize)}, minmax(0, 1fr))` : `repeat(auto-fill, minmax(min(${cardSize}px, 100%), 1fr))` }}>
                {cameras.map((camera) => (
                  <CameraTile
                    key={camera}
                    siteId={siteId}
                    camera={camera}
                    cluster={latestPerCamera.get(`${siteId}:${camera}`) ?? null}
                    status={health[siteId]?.[camera]}
                    tampered={tamper[siteId]?.[camera]}
                    onSelectCamera={onSelectCamera}
                  />
                ))}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

interface TileProps {
  siteId: string
  camera: string
  cluster: Cluster | null
  status: CameraStatus | undefined
  tampered: CameraTamper | undefined
  onSelectCamera: (siteId: string, camera: string) => void
}

function CameraTile({ siteId, camera, cluster, status, tampered, onSelectCamera }: TileProps) {
  // Offline is about the camera, not about whether anything happened, so it
  // outranks the activity state everywhere below.
  const offline = status === 'offline'
  // Openable, unlike offline: it is recording, just at reduced quality.
  const degraded = status === 'degraded'
  // Only where there is no detection to show: its picture is the one worth
  // seeing, and an offline camera has no frame to give.
  const [picture, setPicture] = useState<HTMLDivElement | null>(null)
  const still = useLiveStill(picture, siteId, camera, !offline && !cluster)
  const live = !offline && !cluster ? still : null
  const chips: Chip[] = []
  if (offline) chips.push({ text: 'Offline', className: `${chipOnPicture} text-fault` })
  // Whatever its health: a camera covered before it dropped is still covered
  // when it comes back.
  if (tampered) chips.push({ text: `${tamperLabel(tampered)} · ${relTime(Date.parse(tampered.since) / 1000)}`, className: 'bg-attention text-zinc-950 border border-attention' })
  if (degraded) chips.push({ text: 'Degraded', className: 'bg-black/65 border border-degraded/70 text-degraded' })
  if (!offline && cluster && isInProgress(cluster)) chips.push({ text: 'In progress', className: `${chipOnPicture} text-live`, pulse: isStillGrowing(cluster) })
  if (cluster?.acknowledged) chips.push({ text: 'Acknowledged', className: `${chipOnPicture} text-zinc-100` })
  // Said on the picture, so a live frame is never read as what was detected.
  if (live) chips.push({ text: 'Live', className: `${chipOnPicture} text-zinc-100` })
  const name = [camera, ...chips.map((c) => c.text), offline ? 'nothing to view' : 'open live view'].join(', ')
  return (
    <button
      // There is no stream to open: the player would sit there never
      // starting, leaving the operator to work out whether that is the camera
      // or us.
      disabled={offline}
      aria-label={name}
      data-tile={`${siteId}/${camera}`}
      onClick={() => onSelectCamera(siteId, camera)}
      className={`group text-left bg-zinc-900 border rounded-lg overflow-hidden transition-[border-color,transform] duration-150 ease-out motion-reduce:transition-none ${
        offline
          ? 'border-fault/40 cursor-not-allowed'
          : tampered
            ? 'border-attention/70 hover:border-attention active:scale-[0.98]'
            : 'border-zinc-800 hover:border-zinc-600 active:scale-[0.98]'
      }`}
    >
      <div ref={setPicture} className="relative">
        {offline ? (
          <div className="w-full aspect-video bg-zinc-950 flex flex-col items-center justify-center gap-1.5">
            <OfflineCameraIcon className="w-6 h-6 text-fault" />
            <span className="text-sm font-semibold text-fault">Camera offline</span>
          </div>
        ) : cluster ? (
          <TileThumb cluster={cluster} camera={camera} />
        ) : live ? (
          <img src={live.url} alt={camera} className="w-full aspect-video object-contain bg-zinc-950" />
        ) : (
          <div className="w-full aspect-video bg-zinc-950" />
        )}
        {chips.length > 0 && (
          <div className="absolute top-2 left-2 right-2 flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <span key={c.text} className={`h-6 px-2 inline-flex items-center gap-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${c.className}`}>
                {c.text === 'In progress' && (
                  <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full bg-current ${c.pulse ? 'animate-pulse motion-reduce:animate-none' : ''}`} />
                )}
                {c.text}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <div className="font-semibold text-sm truncate" title={camera}>{camera}</div>
        <div className="text-[13px] mt-1 truncate text-zinc-400 tabular-nums">
          {offline ? (
            <span className="text-fault">Not sending video</span>
          ) : cluster ? (
            [labelOf(cluster), cluster.event_count > 1 ? `${cluster.event_count} detections` : '', relTime(cluster.end_time)].filter(Boolean).join(' · ')
          ) : live ? (
            `Live · ${relTime(live.at)}`
          ) : (
            'No recent activity'
          )}
        </div>
      </div>
    </button>
  )
}

// labelOf is an incident's label as a tile shows it: Frigate's license_plate
// as "License plate".
function labelOf(c: Cluster): string {
  const words = (c.label || 'activity').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
